/**
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

/**
 * useAppRouting, react hook for routing logic for the deep link feature
 *
 * Encapsulates all URL ↔ Redux routing synchronization for the app:
 *  1. Hydration — reads the initial URL on mount and hydrates Redux state.
 *  2. Popstate — keeps Redux in sync when the user navigates with back/forward.
 *  3. URL sync — pushes the correct URL whenever Redux chat-mode state changes.
 *
 * Returns `{ hydrated }` so the caller can gate rendering until the initial
 * URL → Redux hydration pass is complete (prevents a flash of the wrong page).
 */

import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../store/store';
import { setChatMode, setRagChatType, type ChatModeState } from '../store/chatModeSlice';
import { getRagByUrlSlug, getUrlSlugForRag } from '../utils/rag/ragConfig';

export function useAppRouting(): { hydrated: boolean } {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const chatModeState: ChatModeState = useSelector((state: RootState) => state.chatMode);

  // Hydration state: only render main content after initial URL → Redux pass completes
  const [hydrated, setHydrated] = useState(false);

  // Keep a ref to chatModeState so the hydration effect can read current values
  // without being listed as a dependency (prevents re-running on every state change).
  const chatModeRef = useRef(chatModeState);
  chatModeRef.current = chatModeState;

  // navigateRef keeps navigate fresh without putting it in effect deps.
  // useNavigate() returns a new function reference on every render; if navigate
  // were a dep the URL-sync effect would re-run after every navigate() call,
  // causing a spurious second push before window.location has stabilised.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // ─── Effect 1: Hydrate Redux from URL on mount ────────────────────────────
  useEffect(() => {
    let needsHydration = false;
    const match = location.pathname.match(/^\/([^/]+)$/);
    if (match) {
      const segment = decodeURIComponent(match[1]).toLowerCase();
      if (segment === '3m-knowledge') {
        // RAG selector page — 3M Knowledge mode with no specific RAG selected
        if (!(chatModeRef.current.mode === '3M Knowledge' && chatModeRef.current.ragChatType === null)) {
          dispatch(setChatMode('3M Knowledge'));
          dispatch(setRagChatType(null));
          needsHydration = true;
        }
      } else {
        const ragConfig = getRagByUrlSlug(segment);
        if (ragConfig) {
          // Only hydrate if Redux state does not already match the URL
          if (!(chatModeRef.current.mode === '3M Knowledge' && chatModeRef.current.ragChatType === ragConfig.key)) {
            dispatch(setChatMode('3M Knowledge'));
            dispatch(setRagChatType(ragConfig.key));
            needsHydration = true;
          }
        } else {
          // Unknown or disabled RAG — redirect home
          navigate('/');
          if (chatModeRef.current.mode === '3M Knowledge' && chatModeRef.current.ragChatType) {
            dispatch(setChatMode('Ask Chat'));
            dispatch(setRagChatType(null));
            needsHydration = true;
          }
        }
      }
    } else {
      // Not a RAG URL — ensure we are not stuck in RAG mode
      if (chatModeRef.current.mode === '3M Knowledge' && chatModeRef.current.ragChatType) {
        dispatch(setChatMode('Ask Chat'));
        dispatch(setRagChatType(null));
        needsHydration = true;
      }
    }

    if (!needsHydration) {
      setHydrated(true);
    } else {
      // Wait a tick for Redux to update, then mark hydrated
      setTimeout(() => setHydrated(true), 0);
    }
  // Only run on mount — location changes are handled by the popstate listener below
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // ─── Effect 2: Sync Redux when browser back/forward is used ──────────────
  useEffect(() => {
    const handlePopState = () => {
      const match = window.location.pathname.match(/^\/([^/]+)$/);
      if (match) {
        const segment = decodeURIComponent(match[1]).toLowerCase();
        if (segment === '3m-knowledge') {
          // RAG selector page
          dispatch(setChatMode('3M Knowledge'));
          dispatch(setRagChatType(null));
        } else {
          const ragConfig = getRagByUrlSlug(segment);
          if (ragConfig) {
            dispatch(setChatMode('3M Knowledge'));
            dispatch(setRagChatType(ragConfig.key));
          } else {
            // Unknown or disabled RAG — go home
            dispatch(setChatMode('Ask Chat'));
            dispatch(setRagChatType(null));
            navigateRef.current('/');
          }
        }
      } else {
        dispatch(setChatMode('Ask Chat'));
        dispatch(setRagChatType(null));
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [dispatch]);

  // ─── Effect 3: Push URL when Redux chat-mode state changes ───────────────
  useEffect(() => {
    if (chatModeState.mode === '3M Knowledge' && chatModeState.ragChatType) {
      const ragUrlSegment = getUrlSlugForRag(chatModeState.ragChatType);
      if (ragUrlSegment) {
        if (window.location.pathname !== `/${ragUrlSegment}`) {
          navigateRef.current(`/${ragUrlSegment}`);
        }
      } else {
        const fallbackSegment = chatModeState.ragChatType.toLowerCase().replace(/\s+/g, '');
        if (window.location.pathname !== `/${fallbackSegment}`) {
          navigateRef.current(`/${fallbackSegment}`);
        }
      }
    } else if (chatModeState.mode === '3M Knowledge' && !chatModeState.ragChatType) {
      // RAG selector page — use dedicated /3m-knowledge URL
      if (window.location.pathname !== '/3m-knowledge') {
        navigateRef.current('/3m-knowledge');
      }
    } else {
      if (window.location.pathname !== '/') {
        navigateRef.current('/');
      }
    }
  // navigate intentionally excluded — read via navigateRef to prevent re-runs on each navigate() call
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatModeState.mode, chatModeState.ragChatType]);

  return { hydrated };
}
